import React, { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import "@/i18n"; // Initialize i18n
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";

// Pages
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import Bookkeeping from "@/pages/Bookkeeping";
import InvoiceScanner from "@/pages/InvoiceScanner";
import BankImport from "@/pages/BankImport";
import Payroll from "@/pages/Payroll";
import Settings from "@/pages/Settings";
import AIAssistant from "@/pages/AIAssistant";
import Customers from "@/pages/Customers";
import Suppliers from "@/pages/Suppliers";
import SupplierInvoices from "@/pages/SupplierInvoices";
import Articles from "@/pages/Articles";
import SalesInvoices from "@/pages/SalesInvoices";
import Quotes from "@/pages/Quotes";
import Reports from "@/pages/Reports";
import Inkasso from "@/pages/Inkasso";
import TeamManagement from "@/pages/TeamManagement";
import RecurringInvoices from "@/pages/RecurringInvoices";
import EmailScanner from "@/pages/EmailScanner";
import ScheduledTasks from "@/pages/ScheduledTasks";
import FortnoxIntegration from "@/pages/FortnoxIntegration";
import FixedAssets from "@/pages/FixedAssets";
import SwedishIntegrations from "@/pages/SwedishIntegrations";
import Inventory from "@/pages/Inventory";
import Projects from "@/pages/Projects";
import Budgeting from "@/pages/Budgeting";
import CostCenters from "@/pages/CostCenters";
import BankIDSettings from "@/pages/BankIDSettings";

// Mobile Pages
import { 
  MobileDashboard, 
  MobileInvoices, 
  MobileInvoiceCreate,
  MobileScan, 
  MobileReports, 
  MobileMore,
  MobileBookkeeping,
  MobileBookkeepingCreate,
  MobileCustomers,
  MobileCustomerCreate
} from "@/mobile";

// Components
import Sidebar from "@/components/Sidebar";
import { SmartLearningProvider, SmartSuggestionsBar, SmartAssistantButton } from "@/components/SmartLearning";
import { ThemeProvider } from "@/components/ThemeToggle";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error("Auth error:", error);
      localStorage.removeItem("token");
      setToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API}/auth/register`, data);
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem("token", newToken);
    setToken(newToken);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const switchCompany = async (companyId) => {
    try {
      await axios.post(`${API}/companies/${companyId}/switch`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchUser();
      toast.success("Företag bytt");
    } catch (error) {
      toast.error("Kunde inte byta företag");
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, switchCompany, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Laddar...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
};

// Main Layout with Smart Learning
const MainLayout = ({ children }) => {
  return (
    <SmartLearningProvider>
      <div className="flex min-h-screen bg-background transition-colors duration-300">
        <Sidebar />
        <main className="flex-1 ml-[280px]">
          <div className="p-8 max-w-7xl mx-auto">
            <SmartSuggestionsBar />
            {children}
          </div>
        </main>
        <SmartAssistantButton />
      </div>
    </SmartLearningProvider>
  );
};

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/" element={
              <ProtectedRoute>
                <MainLayout>
                  <Dashboard />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/accounts" element={
              <ProtectedRoute>
                <MainLayout>
                  <ChartOfAccounts />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/bookkeeping" element={
              <ProtectedRoute>
                <MainLayout>
                  <Bookkeeping />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/invoices" element={
              <ProtectedRoute>
                <MainLayout>
                  <InvoiceScanner />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/bank" element={
              <ProtectedRoute>
                <MainLayout>
                  <BankImport />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/payroll" element={
              <ProtectedRoute>
                <MainLayout>
                  <Payroll />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <MainLayout>
                  <Settings />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/assistant" element={
              <ProtectedRoute>
                <MainLayout>
                  <AIAssistant />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/customers" element={
              <ProtectedRoute>
                <MainLayout>
                  <Customers />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/suppliers" element={
              <ProtectedRoute>
                <MainLayout>
                  <Suppliers />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/supplier-invoices" element={
              <ProtectedRoute>
                <MainLayout>
                  <SupplierInvoices />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/articles" element={
              <ProtectedRoute>
                <MainLayout>
                  <Articles />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/sales-invoices" element={
              <ProtectedRoute>
                <MainLayout>
                  <SalesInvoices />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/quotes" element={
              <ProtectedRoute>
                <MainLayout>
                  <Quotes />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/reports" element={
              <ProtectedRoute>
                <MainLayout>
                  <Reports />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/inkasso" element={
              <ProtectedRoute>
                <MainLayout>
                  <Inkasso />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/team" element={
              <ProtectedRoute>
                <MainLayout>
                  <TeamManagement />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/abonnemang" element={
              <ProtectedRoute>
                <MainLayout>
                  <RecurringInvoices />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/email-scanner" element={
              <ProtectedRoute>
                <MainLayout>
                  <EmailScanner />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/scheduler" element={
              <ProtectedRoute>
                <MainLayout>
                  <ScheduledTasks />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fortnox" element={
              <ProtectedRoute>
                <MainLayout>
                  <FortnoxIntegration />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/fixed-assets" element={
              <ProtectedRoute>
                <MainLayout>
                  <FixedAssets />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/swedish-integrations" element={
              <ProtectedRoute>
                <MainLayout>
                  <SwedishIntegrations />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/inventory" element={
              <ProtectedRoute>
                <MainLayout>
                  <Inventory />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/projects" element={
              <ProtectedRoute>
                <MainLayout>
                  <Projects />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/budgeting" element={
              <ProtectedRoute>
                <MainLayout>
                  <Budgeting />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/cost-centers" element={
              <ProtectedRoute>
                <MainLayout>
                  <CostCenters />
                </MainLayout>
              </ProtectedRoute>
            } />
            <Route path="/bankid" element={
              <ProtectedRoute>
                <MainLayout>
                  <BankIDSettings />
                </MainLayout>
              </ProtectedRoute>
            } />
            
            {/* Mobile Routes - Standalone without Sidebar */}
            <Route path="/m" element={<ProtectedRoute><MobileDashboard /></ProtectedRoute>} />
            <Route path="/m/invoices" element={<ProtectedRoute><MobileInvoices /></ProtectedRoute>} />
            <Route path="/m/invoices/new" element={<ProtectedRoute><MobileInvoiceCreate /></ProtectedRoute>} />
            <Route path="/m/invoices/:id" element={<ProtectedRoute><MobileInvoices /></ProtectedRoute>} />
            <Route path="/m/scan" element={<ProtectedRoute><MobileScan /></ProtectedRoute>} />
            <Route path="/m/reports" element={<ProtectedRoute><MobileReports /></ProtectedRoute>} />
            <Route path="/m/more" element={<ProtectedRoute><MobileMore /></ProtectedRoute>} />
            <Route path="/m/customers" element={<ProtectedRoute><MobileCustomers /></ProtectedRoute>} />
            <Route path="/m/customers/new" element={<ProtectedRoute><MobileCustomerCreate /></ProtectedRoute>} />
            <Route path="/m/customers/:id" element={<ProtectedRoute><MobileCustomers /></ProtectedRoute>} />
            <Route path="/m/bookkeeping" element={<ProtectedRoute><MobileBookkeeping /></ProtectedRoute>} />
            <Route path="/m/bookkeeping/new" element={<ProtectedRoute><MobileBookkeepingCreate /></ProtectedRoute>} />
            <Route path="/m/bookkeeping/:id" element={<ProtectedRoute><MobileBookkeeping /></ProtectedRoute>} />
            <Route path="/m/notifications" element={<ProtectedRoute><MobileMore /></ProtectedRoute>} />
          </Routes>
          <Toaster position="top-right" richColors />
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
